import type { Enemy } from '../Enemy';
import type { MovementBehavior } from './MovementBehavior';
import { getEnemyMovementTopY, getEnemyMovementBottomY } from '../../constants/field';
import { getEnemyMobileSpeedMultiplier } from '../../constants/scale';

export class RandomZigzagBehavior implements MovementBehavior {
  // 0 = вправо, +π/2 = вниз, -π/2 = вверх
  private currentAngleRad = 0;
  private timeToNextChangeMs = 0;
  private elapsedSinceChangeMs = 0;

  private minChangeIntervalMs = 800;
  private maxChangeIntervalMs = 1200;
  private maxAngleDeltaDeg = 60;
  private minAngleDeltaDeg = 30;
  private maxAngleFromForward = 70;

  init(_enemy: Enemy, params?: Record<string, number>): void {
    if (params) {
      this.minChangeIntervalMs = params.minChangeIntervalMs ?? this.minChangeIntervalMs;
      this.maxChangeIntervalMs = params.maxChangeIntervalMs ?? this.maxChangeIntervalMs;
      this.maxAngleDeltaDeg = params.maxAngleDeltaDeg ?? this.maxAngleDeltaDeg;
      this.minAngleDeltaDeg = params.minAngleDeltaDeg ?? this.minAngleDeltaDeg;
      this.maxAngleFromForward = params.maxAngleFromForward ?? this.maxAngleFromForward;
    }
    // Стартовое направление — слегка под углом вверх или вниз
    this.currentAngleRad =
      (Math.random() - 0.5) * 2 * ((this.maxAngleFromForward * Math.PI) / 180);
    this.scheduleNextChange();
  }

  private scheduleNextChange(): void {
    this.timeToNextChangeMs =
      this.minChangeIntervalMs +
      Math.random() * (this.maxChangeIntervalMs - this.minChangeIntervalMs);
    this.elapsedSinceChangeMs = 0;
  }

  private changeDirection(): void {
    const deltaDeg =
      this.minAngleDeltaDeg +
      Math.random() * (this.maxAngleDeltaDeg - this.minAngleDeltaDeg);
    const sign = Math.random() < 0.5 ? -1 : 1;

    this.currentAngleRad += (sign * deltaDeg * Math.PI) / 180;

    const maxRad = (this.maxAngleFromForward * Math.PI) / 180;
    if (this.currentAngleRad > maxRad) this.currentAngleRad = maxRad;
    if (this.currentAngleRad < -maxRad) this.currentAngleRad = -maxRad;

    this.scheduleNextChange();
  }

  update(enemy: Enemy, deltaMs: number, _target: { x: number; y: number }): void {
    this.elapsedSinceChangeMs += deltaMs;
    if (this.elapsedSinceChangeMs >= this.timeToNextChangeMs) {
      this.changeDirection();
    }

    const effectiveSpeed = enemy.config.speed * getEnemyMobileSpeedMultiplier();
    const moveDistance = (effectiveSpeed * deltaMs) / 1000;
    enemy.x += Math.cos(this.currentAngleRad) * moveDistance;
    enemy.y += Math.sin(this.currentAngleRad) * moveDistance;

    // Отскок от верхней/нижней границы поля.
    const halfSize = enemy.displayWidth / 2;
    const minY = getEnemyMovementTopY(enemy.scene) + halfSize;
    const maxY = getEnemyMovementBottomY(enemy.scene) - halfSize;

    if (enemy.y < minY) {
      enemy.y = minY;
      this.currentAngleRad = -this.currentAngleRad;
      this.scheduleNextChange();
    } else if (enemy.y > maxY) {
      enemy.y = maxY;
      this.currentAngleRad = -this.currentAngleRad;
      this.scheduleNextChange();
    }
  }
}
